import React, { useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/Sidebar';
import axios from '../services/axios';
import Modal from '../components/Modal';
import { formatApiDate, getTodayDateInputValue } from '../utils/date';
import { usePointCobro } from '../hooks/usePointCobro';
import PointCobroControls from '../components/point/PointCobroControls';
import PointCobroResultModal from '../components/point/PointCobroResultModal';
import PointCobroWaitingModal from '../components/point/PointCobroWaitingModal';

const estadoBadge = (estado) => {
  switch (estado) {
    case 'pendiente':
      return 'bg-amber-100 text-amber-800';
    case 'pagada':
      return 'bg-emerald-100 text-emerald-800';
    case 'procesando':
      return 'bg-blue-100 text-blue-800';
    case 'vencida':
      return 'bg-red-100 text-red-700';
    case 'plan_pago':
      return 'bg-indigo-100 text-indigo-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const estadoLabel = (estado) => {
  switch (estado) {
    case 'pendiente':
      return 'Pendiente';
    case 'pagada':
      return 'Pagada';
    case 'procesando':
      return 'Procesando';
    case 'vencida':
      return 'Vencida';
    case 'plan_pago':
      return 'Plan de pago';
    default:
      return estado || 'N/A';
  }
};

const formatearMonto = (monto) => Number(monto || 0).toFixed(2);
const formatearFecha = (fecha) => (fecha ? formatApiDate(fecha) : '-');
const hoyStr = () => getTodayDateInputValue();
const ordenarBoletas = (lista) => [...(lista || [])].sort(
  (a, b) =>
    new Date(b.fechaEmision || b.createdAt || 0).getTime() -
    new Date(a.fechaEmision || a.createdAt || 0).getTime(),
);

function CobrosEfectivo() {
  const [padron, setPadron] = useState('');
  const [boletaIdInput, setBoletaIdInput] = useState('');
  const [cliente, setCliente] = useState(null);
  const [boletas, setBoletas] = useState([]);
  const [selectedBoleta, setSelectedBoleta] = useState(null);
  const [montoCobro, setMontoCobro] = useState('');
  const [fechaPago, setFechaPago] = useState(hoyStr());
  const [observaciones, setObservaciones] = useState('');
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [facturar, setFacturar] = useState(true);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const point = usePointCobro(selectedBoleta?.id, id => refrescarBoleta(id));
  useEffect(() => {
    if (point.cobro?.activo && point.cobro.boletaId === selectedBoleta?.id && metodoPago !== 'point') {
      setMetodoPago('point');
    }
  }, [point.cobro?.activo, point.cobro?.boletaId, selectedBoleta?.id, metodoPago]);
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    confirmText: 'Aceptar',
    onConfirm: null,
    onCancel: null,
    cancelText: null,
  });

  const cerrarModal = () =>
    setModal((prev) => ({
      ...prev,
      isOpen: false,
      onConfirm: null,
      onCancel: null,
      cancelText: null,
    }));

  const mostrarModal = (config) =>
    setModal({
      isOpen: true,
      type: config.type || 'info',
      title: config.title || '',
      message: config.message || '',
      confirmText: config.confirmText || 'Aceptar',
      onConfirm: config.onConfirm,
      onCancel: config.onCancel,
      cancelText: config.cancelText,
    });

  const finalizarCobroPoint = () => {
    point.limpiar();
    cerrarModal();
    setPadron(''); setBoletaIdInput(''); setCliente(null); setBoletas([]); setSelectedBoleta(null);
    setMontoCobro(''); setFechaPago(hoyStr()); setObservaciones(''); setMetodoPago('efectivo'); setFacturar(true);
  };

  const seleccionarBoleta = (boleta) => {
    setSelectedBoleta(boleta);
    setMontoCobro(Number(boleta.total) > 0 ? formatearMonto(boleta.total) : '');
    setFechaPago(hoyStr());
    setObservaciones('');
    setMetodoPago('efectivo');
  };

  const buscar = async () => {
    if (!padron && !boletaIdInput) {
      mostrarModal({
        type: 'warning',
        title: 'Falta información',
        message: 'Ingresa un padrón o un número de boleta para buscar.',
        onConfirm: cerrarModal,
      });
      return;
    }

    setLoading(true);
    setCliente(null);
    setBoletas([]);
    setSelectedBoleta(null);

    try {
      if (boletaIdInput) {
        const boletaRes = await axios.get(`/boletas/${boletaIdInput}`);
        const boleta = boletaRes.data;
        setBoletas([boleta]);
        seleccionarBoleta(boleta);
        setCliente(boleta.usuario || null);
      } else {
        const clienteRes = await axios.get(`/usuarios/padron/${padron}`);
        const usuario = clienteRes.data;
        setCliente(usuario);

        const boletasRes = await axios
          .get(`/boletas/usuario/${usuario.id}`)
          .catch(() => ({ data: [] }));

        const listaOrdenada = ordenarBoletas(boletasRes.data);

        setBoletas(listaOrdenada);

        const primera =
          listaOrdenada.find((b) => b.estado !== 'pagada' && Number(b.total) > 0) ||
          listaOrdenada.find((b) => b.estado !== 'pagada') ||
          listaOrdenada[0] ||
          null;
        if (primera) {
          seleccionarBoleta(primera);
        }
      }
    } catch (error) {
      mostrarModal({
        type: 'error',
        title: 'No se encontró información',
        message:
          error.response?.data?.message ||
          'No se pudo obtener datos del cliente o boleta. Verifica la información e intenta nuevamente.',
        onConfirm: cerrarModal,
      });
    } finally {
      setLoading(false);
    }
  };

  const refrescarBoleta = async (boletaId) => {
    try {
      const res = await axios.get(`/boletas/${boletaId}`);
      const actualizada = res.data;
      setBoletas((prev) =>
        prev.map((b) => (b.id === actualizada.id ? actualizada : b)),
      );
      setSelectedBoleta(prev => prev?.id === actualizada.id ? actualizada : prev);
    } catch (_) {
      // mantener estado local si falla la recarga
    }
  };

  const ejecutarCobro = async () => {
    if (metodoPago === 'point') {
      await point.crear({ observaciones, facturar });
      return;
    }
    if (!selectedBoleta) {
      mostrarModal({
        type: 'warning',
        title: 'Selecciona una boleta',
        message: 'Debes elegir una boleta para registrar el cobro.',
        onConfirm: cerrarModal,
      });
      return;
    }

    setDetailLoading(true);
    try {
      await axios.post('/pagos/efectivo', {
        boletaId: selectedBoleta.id,
        monto: Number(montoCobro),
        fechaPago,
        observaciones,
        facturar,
        metodoPago,
      });

      await refrescarBoleta(selectedBoleta.id);

      const medioExito = metodoPago === 'transferencia' ? 'transferencia' : 'efectivo';
      mostrarModal({
        type: 'success',
        title: 'Cobro registrado',
        message: `El pago por ${medioExito} se guardó y la boleta quedó marcada como PAGADA.`,
        onConfirm: cerrarModal,
      });
    } catch (error) {
      mostrarModal({
        type: 'error',
        title: 'No se pudo registrar el cobro',
        message: error.response?.data?.message || 'Ocurrió un error al intentar guardar el cobro en efectivo.',
        onConfirm: cerrarModal,
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const confirmarCobro = () => {
    if (!selectedBoleta) {
      mostrarModal({
        type: 'warning',
        title: 'Selecciona una boleta',
        message: 'Elige la boleta a cobrar antes de continuar.',
        onConfirm: cerrarModal,
      });
      return;
    }

    if (selectedBoleta.estado === 'pagada') {
      mostrarModal({
        type: 'warning',
        title: 'Boleta ya pagada',
        message: 'Esta boleta ya se encuentra pagada. Selecciona otra boleta pendiente.',
        onConfirm: cerrarModal,
      });
      return;
    }

    if (Number(selectedBoleta.total) <= 0) {
      mostrarModal({
        type: 'info',
        title: 'Saldo a favor',
        message: 'Esta boleta representa saldo a favor. Selecciona una boleta con importe pendiente para aplicarlo.',
        onConfirm: cerrarModal,
      });
      return;
    }

    const montoNumero = Number(metodoPago === 'point' ? selectedBoleta.total : montoCobro);
    if (Number.isNaN(montoNumero) || montoNumero <= 0) {
      mostrarModal({
        type: 'warning',
        title: 'Monto inválido',
        message: 'Ingresa un monto válido para registrar el cobro.',
        onConfirm: cerrarModal,
      });
      return;
    }

    const medioLabel = metodoPago === 'point' ? 'Posnet' : metodoPago === 'transferencia' ? 'transferencia' : 'efectivo';
    mostrarModal({
      type: 'info',
      title: 'Confirmar cobro presencial',
      message: `¿Registrar el cobro por ${medioLabel} de $${formatearMonto(montoNumero)} para la boleta ${selectedBoleta.mes}/${selectedBoleta.anio} del padrón ${selectedBoleta.usuario?.padron || ''}?`,
      confirmText: 'Registrar cobro',
      cancelText: 'Cancelar',
      onConfirm: () => {
        cerrarModal();
        ejecutarCobro();
      },
      onCancel: cerrarModal,
    });
  };

  const ejecutarAplicacionSaldoFavor = async () => {
    if (!selectedBoleta) return;

    setDetailLoading(true);
    try {
      const response = await axios.post('/pagos/aplicar-saldo-favor', {
        boletaId: selectedBoleta.id,
      });

      const usuarioId = selectedBoleta.usuario?.id || cliente?.id;
      if (usuarioId) {
        const boletasRes = await axios.get(`/boletas/usuario/${usuarioId}`);
        const listaOrdenada = ordenarBoletas(boletasRes.data);
        setBoletas(listaOrdenada);
        const actualizada = listaOrdenada.find((boleta) => boleta.id === selectedBoleta.id);
        if (actualizada) seleccionarBoleta(actualizada);
      }

      const resultado = response.data;
      mostrarModal({
        type: 'success',
        title: 'Saldo aplicado',
        message: resultado.boletaPagada
          ? `Se aplicaron $${formatearMonto(resultado.montoAplicado)}. La boleta quedó pagada sin generar factura. Saldo restante: $${formatearMonto(resultado.saldoRestante)}.`
          : `Se aplicaron $${formatearMonto(resultado.montoAplicado)}. Quedan $${formatearMonto(resultado.totalRestante)} por cobrar y $${formatearMonto(resultado.saldoRestante)} de saldo a favor.`,
        onConfirm: cerrarModal,
      });
    } catch (error) {
      mostrarModal({
        type: 'error',
        title: 'No se pudo aplicar el saldo',
        message: error.response?.data?.message || 'Ocurrió un error al aplicar el saldo a favor.',
        onConfirm: cerrarModal,
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const confirmarAplicacionSaldoFavor = () => {
    if (!selectedBoleta || Number(selectedBoleta.total) <= 0) return;

    const montoAplicable = Math.min(saldoFavorDisponible, Number(selectedBoleta.total));
    mostrarModal({
      type: 'info',
      title: 'Aplicar saldo a favor',
      message: `¿Aplicar $${formatearMonto(montoAplicable)} de saldo a favor a la boleta ${selectedBoleta.mes}/${selectedBoleta.anio}? No se registrará ningún movimiento de caja.`,
      confirmText: 'Aplicar saldo',
      cancelText: 'Cancelar',
      onConfirm: () => {
        cerrarModal();
        ejecutarAplicacionSaldoFavor();
      },
      onCancel: cerrarModal,
    });
  };

  const boletasPendientes = useMemo(
    () => boletas.filter((b) => b.estado !== 'pagada' && Number(b.total) > 0),
    [boletas],
  );

  const saldoFavorDisponible = useMemo(
    () => Number(
      boletas
        .filter((b) => !['pagada', 'anulada'].includes(b.estado) && Number(b.total) < 0)
        .reduce((total, boleta) => total + Math.abs(Number(boleta.total)), 0)
        .toFixed(2),
    ),
    [boletas],
  );

  const selectedEsSaldoFavor = Number(selectedBoleta?.total) < 0;
  const esperandoPoint = !point.resultado && Boolean(point.loading || point.cobro?.activo);
  const pointModalOpen = esperandoPoint || Boolean(point.resultado);

  return (
    <>
    <div className="flex h-screen bg-[#f8f9fa]" inert={pointModalOpen ? '' : undefined}>
      {menuAbierto && <button type="button" aria-label="Cerrar" onClick={() => setMenuAbierto(false)}
        className="fixed inset-0 z-30 bg-black/30 lg:hidden" />}
      <div className={menuAbierto ? 'fixed inset-y-0 left-0 z-40 lg:static' : 'hidden lg:block'}>
        <Sidebar />
        {menuAbierto && <button type="button" aria-label="Cerrar menú" onClick={() => setMenuAbierto(false)}
          className="absolute right-2 top-2 rounded-lg bg-white p-2 lg:hidden">
          <span className="material-symbols-outlined">close</span>
        </button>}
      </div>

      <main className="min-w-0 flex-1 overflow-auto">
        <div className="p-4 sm:p-8">
          <button type="button" aria-label="Menú" onClick={() => setMenuAbierto(true)}
            className="mb-3 rounded-lg border bg-white p-2 lg:hidden">
            <span className="material-symbols-outlined">menu</span>
          </button>
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-sm text-[#617c89]">Caja • Cobros presenciales</p>
              <h1 className="text-3xl font-bold text-[#111618]">Cobros en efectivo</h1>
              <p className="text-gray-600">Registra pagos de boletas abonadas en el local.</p>
            </div>
            <div className="hidden md:flex gap-2">
              <div className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600">
                <span className="font-semibold">{boletasPendientes.length}</span> boletas pendientes del resultado
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 space-y-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs uppercase text-[#617c89]">Búsqueda</p>
                    <h2 className="text-lg font-semibold text-[#111618]">Encontrar boleta</h2>
                  </div>
                  <button
                    onClick={buscar}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:bg-gray-300"
                  >
                    <span className="material-symbols-outlined text-lg">search</span>
                    {loading ? 'Buscando...' : 'Buscar'}
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Padrón del cliente
                    </label>
                    <input
                      type="text"
                      value={padron}
                      onChange={(e) => setPadron(e.target.value)}
                      placeholder="Ej: 1234"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      También puedes buscar por número de boleta si lo tienes a mano.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Número de boleta
                    </label>
                    <input
                      type="number"
                      value={boletaIdInput}
                      onChange={(e) => setBoletaIdInput(e.target.value)}
                      placeholder="Ej: 1023"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                {cliente && (
                  <div className="mt-4 flex items-center gap-3 text-sm text-gray-700 bg-gray-50 rounded-lg p-3">
                    <span className="material-symbols-outlined text-primary">person</span>
                    <div>
                      <p className="font-semibold">{cliente.nombre}</p>
                      <p className="text-xs text-gray-500">
                        Padrón {cliente.padron} {cliente.zona ? `• ${cliente.zona.nombre}` : ''}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase text-[#617c89]">Resultados</p>
                    <h2 className="text-lg font-semibold text-[#111618]">Boletas encontradas</h2>
                  </div>
                  <div className="text-sm text-gray-500">
                    {boletas.length === 0
                      ? 'Sin resultados'
                      : `${boletas.length} boleta${boletas.length > 1 ? 's' : ''}`}
                  </div>
                </div>

                {loading ? (
                  <div className="py-10 text-center text-gray-600">Buscando boletas...</div>
                ) : boletas.length === 0 ? (
                  <div className="py-10 text-center text-gray-500">
                    Ingresa un padrón o una boleta para ver resultados.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-6 py-3 text-left font-medium">Boleta</th>
                          <th className="px-6 py-3 text-left font-medium">Vencimiento</th>
                          <th className="px-6 py-3 text-left font-medium">Total</th>
                          <th className="px-6 py-3 text-left font-medium">Estado</th>
                          <th className="px-6 py-3 text-right font-medium">Acción</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {boletas.map((boleta) => (
                          <tr key={boleta.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4">
                              <p className="text-sm font-semibold text-[#111618]">
                                {boleta.mes}/{boleta.anio}
                              </p>
                              <p className="text-xs text-gray-500">ID #{boleta.id}</p>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {formatearFecha(boleta.fechaVencimiento)}
                            </td>
                            <td className={`px-6 py-4 text-sm font-semibold ${Number(boleta.total) < 0 ? 'text-emerald-700' : 'text-[#111618]'}`}>
                              {Number(boleta.total) < 0
                                ? `Saldo $${formatearMonto(Math.abs(Number(boleta.total)))}`
                                : `$${formatearMonto(boleta.total)}`}
                            </td>
                            <td className="px-6 py-4">
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${Number(boleta.total) < 0 ? 'bg-emerald-100 text-emerald-800' : estadoBadge(boleta.estado)}`}
                              >
                                {Number(boleta.total) < 0 ? 'Saldo a favor' : estadoLabel(boleta.estado)}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={() => seleccionarBoleta(boleta)}
                                className="text-primary hover:text-primary/80 font-medium text-sm"
                              >
                                Seleccionar
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs uppercase text-[#617c89]">Cobro</p>
                  <h2 className="text-lg font-semibold text-[#111618]">Registrar pago presencial</h2>
                </div>
                {detailLoading && (
                  <span className="material-symbols-outlined text-primary animate-spin">refresh</span>
                )}
              </div>

              {!selectedBoleta ? (
                <div className="text-center text-gray-500 py-10">
                  Selecciona una boleta para continuar.
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs uppercase text-[#617c89]">Boleta</p>
                        <p className="text-xl font-bold text-[#111618]">
                          {selectedBoleta.mes}/{selectedBoleta.anio}
                        </p>
                        <p className="text-sm text-gray-600">
                          Vence {formatearFecha(selectedBoleta.fechaVencimiento)}
                        </p>
                        {selectedBoleta.fechaPago && (
                          <p className="text-xs text-gray-500 mt-1">
                            Último pago registrado: {formatearFecha(selectedBoleta.fechaPago)}
                          </p>
                        )}
                      </div>
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${selectedEsSaldoFavor ? 'bg-emerald-100 text-emerald-800' : estadoBadge(selectedBoleta.estado)}`}
                      >
                        {selectedEsSaldoFavor ? 'Saldo a favor' : estadoLabel(selectedBoleta.estado)}
                      </span>
                    </div>
                    <div className="mt-3 flex justify-between text-sm">
                      <span className="text-gray-600">{selectedEsSaldoFavor ? 'Saldo disponible' : 'Total a pagar'}</span>
                      <span className={`font-semibold ${selectedEsSaldoFavor ? 'text-emerald-700' : 'text-[#111618]'}`}>
                        ${formatearMonto(selectedEsSaldoFavor ? Math.abs(Number(selectedBoleta.total)) : selectedBoleta.total)}
                      </span>
                    </div>
                  </div>

                  {selectedEsSaldoFavor ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                      <p className="font-semibold">Este importe es dinero a favor del cliente.</p>
                      <p className="mt-1 text-emerald-800">
                        Se conservará en esta boleta hasta aplicarlo a una boleta con importe pendiente.
                      </p>
                    </div>
                  ) : saldoFavorDisponible > 0 ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                      <p className="text-sm font-semibold text-emerald-900">
                        Saldo a favor disponible: ${formatearMonto(saldoFavorDisponible)}
                      </p>
                      <p className="mt-1 text-xs text-emerald-800">
                        Puedes aplicarlo antes de registrar un cobro presencial.
                      </p>
                      <button
                        type="button"
                        onClick={confirmarAplicacionSaldoFavor}
                        disabled={detailLoading || point.recovering || point.cobro?.activo || selectedBoleta.estado === 'pagada'}
                        className="mt-3 w-full rounded-lg bg-emerald-700 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:bg-gray-300"
                      >
                        Aplicar saldo a favor
                      </button>
                    </div>
                  ) : null}

                  <div className={selectedEsSaldoFavor ? 'hidden' : 'space-y-4'}>
                    <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Medio de pago</label>
                    <div className={`grid gap-2 ${point.enabled ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      <button
                        type="button"
                        onClick={() => setMetodoPago('efectivo')}
                        disabled={point.cobro?.activo}
                        className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                          metodoPago === 'efectivo'
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
                        }`}
                      >
                        <span className="material-symbols-outlined text-base">payments</span>
                        Efectivo
                      </button>
                      <button
                        type="button"
                        onClick={() => setMetodoPago('transferencia')}
                        disabled={point.cobro?.activo}
                        className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                          metodoPago === 'transferencia'
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
                        }`}
                      >
                        <span className="material-symbols-outlined text-base">account_balance</span>
                        Transferencia
                      </button>
                      {point.enabled && <button type="button" onClick={() => setMetodoPago('point')}
                        className={`flex items-center justify-center gap-2 py-2 px-2 rounded-lg border text-sm font-medium transition-colors ${metodoPago === 'point' ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300 hover:border-primary'}`}>
                        <span className="material-symbols-outlined text-base">point_of_sale</span>Posnet
                      </button>}
                    </div>
                    </div>
                    {(metodoPago === 'point' || point.cobro?.activo || point.cobro?.incidencia) && <PointCobroControls point={point} />}

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {metodoPago === 'point' ? 'Posnet' : metodoPago === 'transferencia' ? 'Monto cobrado por transferencia' : 'Monto cobrado en efectivo'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={metodoPago === 'point' ? formatearMonto(selectedBoleta.total) : montoCobro}
                      readOnly={metodoPago === 'point'}
                      onChange={(e) => setMontoCobro(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    {metodoPago !== 'point' && <p className="text-xs text-gray-500">
                      Usa el total de la boleta o ajusta si incluye recargos adicionales.
                    </p>}
                  </div>

                  {metodoPago !== 'point' && <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Fecha de pago</label>
                    <input
                      type="date"
                      value={fechaPago}
                      onChange={(e) => setFechaPago(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>}

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Observaciones</label>
                    <textarea
                      rows={3}
                      disabled={point.loading || point.cobro?.activo}
                      value={observaciones}
                      onChange={(e) => setObservaciones(e.target.value)}
                      placeholder="Ej: Pago en ventanilla, incluye mora..."
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    />
                  </div>

                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Facturar al ARCA</p>
                      {metodoPago !== 'point' && <p className="text-xs text-gray-500">Genera comprobante electrónico al registrar el cobro</p>}
                    </div>
                    <button
                      type="button"
                      disabled={point.loading || point.cobro?.activo}
                      onClick={() => setFacturar((v) => !v)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${facturar ? 'bg-primary' : 'bg-gray-300'}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${facturar ? 'translate-x-6' : 'translate-x-1'}`}
                      />
                    </button>
                  </div>

                  <button
                    onClick={confirmarCobro}
                    disabled={detailLoading || point.loading || point.recovering || point.cobro?.activo ||
                      (metodoPago === 'point' && (!point.terminalId || !['pendiente', 'vencida'].includes(selectedBoleta.estado))) ||
                      selectedBoleta.estado === 'pagada' || Number(selectedBoleta.total) <= 0}
                    className="w-full flex items-center justify-center gap-2 h-12 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    <span className="material-symbols-outlined">point_of_sale</span>
                    {metodoPago === 'point' ? 'Posnet' : metodoPago === 'transferencia' ? 'Registrar cobro por transferencia' : 'Registrar cobro en efectivo'}
                  </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>

      <Modal
        isOpen={modal.isOpen && !pointModalOpen}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        confirmText={modal.confirmText}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
        cancelText={modal.cancelText}
      />
      <PointCobroWaitingModal isOpen={esperandoPoint} point={point} monto={selectedBoleta?.total} />
      <PointCobroResultModal resultado={point.resultado} onClose={finalizarCobroPoint} />
    </>
  );
}

export default CobrosEfectivo;
